import { useMemo } from "react";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";

export interface AuditTag {
  id: Id<"auditTags">;
  name: string;
  color: string;
}

interface UseAuditTagsReturn {
  tags: AuditTag[] | undefined;
  isLoading: boolean;
}

export function useAuditTags(): UseAuditTagsReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.audit_queries.getAllAuditTags,
    {},
  );

  return { tags: data as AuditTag[] | undefined, isLoading };
}

/* ------------------------------------------------------------------ */
/* useCreateAuditTag                                                   */
/* ------------------------------------------------------------------ */

export function useCreateAuditTag() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_tag_mutations.createAuditTag, {
    name: "create-audit-tag",
    successMessage: "Tag created",
  });

  return (args: { name: string; color: string }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useUpdateAuditTag                                                  */
/* ------------------------------------------------------------------ */

export function useUpdateAuditTag() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_tag_mutations.updateAuditTag, {
    name: "update-audit-tag",
    successMessage: "Tag updated",
  });

  return (args: { id: Id<"auditTags">; name?: string; color?: string }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useDeleteAuditTag                                                  */
/* ------------------------------------------------------------------ */

export function useDeleteAuditTag() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_tag_mutations.deleteAuditTag, {
    name: "delete-audit-tag",
    successMessage: "Tag deleted",
  });

  return (args: { id: Id<"auditTags"> }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useUpdateAuditTags (assign tags to an audit)                        */
/* ------------------------------------------------------------------ */

export function useUpdateAuditTags() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_mutations.updateAuditTags, {
    name: "update-audit-tags",
    successMessage: "Tags updated",
  });

  return (args: { auditId: Id<"audits">; tagIds: Id<"auditTags">[] }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useAuditTagUsageCounts                                              */
/* ------------------------------------------------------------------ */

export function useAuditTagUsageCounts() {
  const { data, isLoading } = useWorkspaceQuery(
    api.audit_queries.getAuditTagUsageCounts,
    {},
  );

  const usageMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;
    for (const item of data) {
      map.set(item.id, item.usageCount);
    }
    return map;
  }, [data]);

  return { usageMap, isLoading };
}
